import { useEffect, useRef, useState } from "react";

import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../lib/hooks/useAgreementState";
import { useAcceptedAgreements } from "../../context/Agreement";

const Nfc = () => {
  const { t } = useTranslation(["nfc"]);
  const navigate = useNavigate();
  const { action } = useParams<{
    action: "checkin" | "checkout";
  }>();
  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/nfc\/${action}\/?$`).test(pathname);

  const { universalAgreements, showFirst } = useAgreementState();
  const { accept } = useAcceptedAgreements();

  /**
   * The badge has not been held to the reader yet, so only documents meant for
   * every member type can go up here — the rest wait until the scan resolves.
   */
  const pendingAgreements =
    action === "checkin" && showFirst && mainRoute ? universalAgreements : [];

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pendingAgreements.length === 0) {
      inputRef.current?.focus();
    }
  }, [pendingAgreements]);

  useEffect(() => {
    if (pendingAgreements.length > 0) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (code) {
      const pathname = `/nfc/${action}/${code}`;
      timerRef.current = setTimeout(() => {
        navigate(pathname);
        setCode("");
        timerRef.current = null;
      }, 150);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [code, action, navigate, pendingAgreements]);

  if (pendingAgreements.length > 0) {
    return (
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => navigate("/")}
        onAccept={accept}
      />
    );
  }

  return (
    <div className="d-flex align-items-center flex-column">
      <BackButton to="/access/option/checkin" />
      <Title className="page-title home mb-5">
        <Typography variant="h1" className="mb-5">
          {t(`nfc:${action as "checkin" | "checkout"}.title`)}
        </Typography>
        <Typography variant="h3" className="font-weight-regular mt-5 pt-5">
          {t(`nfc:${action as "checkin" | "checkout"}.subtitle`)}
        </Typography>
      </Title>
      <input
        ref={inputRef}
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.trim())}
        className="absolute opacity-0 pointer-events-none"
      />
      <Outlet />
    </div>
  );
};

export default Nfc;
